import { AHSeparator, AHText, Box } from "arkh";
import { FlatList, Platform, View } from "react-native";
import { FormattedMessage } from "react-intl";
import React, { useCallback, useMemo } from "react";
import { StorecastDeviceListItem } from "./StorecastDeviceItem";

import { useLazyLoadQuery, graphql } from "react-relay";
import { StorecastAdminDeviceListQuery } from "./__generated__/StorecastAdminDeviceListQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { StoreAdminNavigationProp } from "../../StoreAdmin/StoreAdminNavigation";
import { AddScreenComponent } from "../AddScreen/AddScreen";
import { AddScreenWebNotice } from "../AddScreenWebNotice";
import { ActionFooter } from "@/src/components/ActionFooter";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { TipArea } from "@/src/components";

type StorecastDeviceListProps = {
  navigation: StoreAdminNavigationProp<"ScreenList">;
};

const StorecastAdminDeviceListComponent = (
  props: StorecastDeviceListProps & { fetchKey: string }
) => {
  const data = useLazyLoadQuery<StorecastAdminDeviceListQuery>(
    graphql`
      query StorecastAdminDeviceListQuery {
        store {
          id
        }
        storecast {
          screens {
            id
            name
            activeSession {
              id
            }
            ...StorecastDeviceItem_StorecastScreen
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const listData = useMemo(
    () =>
      (data?.storecast.screens ?? []).slice().sort((a, b) => {
        const hasA = !!a.activeSession;
        const hasB = !!b.activeSession;
        return hasA === hasB ? a.name.localeCompare(b.name) : hasA ? -1 : 1;
      }),
    [data]
  );

  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <StorecastDeviceListItem
          onPress={() =>
            props.navigation.navigateDeprecated("ScreenAdmin", {
              screen: {
                id: item.id,
                name: item.name,
              },
            })
          }
          storecastScreen={item}
        />
      );
    },
    [props.navigation]
  );

  const intl_tokens = useIntlTokens();
  if (listData.length === 0) {
    if (Platform.OS === "web") {
      return <AddScreenWebNotice />;
    } else {
      return <AddScreenComponent navigation={props.navigation} />;
    }
  }

  const canAdd = Platform.OS !== "web";
  return (
    <View style={{ flex: 1 }}>
      {!canAdd ? (
        <Box spaceHorizontal="medium" spaceVertical="medium">
          <TipArea>
            <AHText style={{ flex: 1 }} color="darkerText" type="body">
              <FormattedMessage
                description="StorecastAdminDeviceList web notice"
                defaultMessage="To add a new screen, we need to scan a QR code. Access the app through your mobile or tablet to continue."
              />
            </AHText>
          </TipArea>
        </Box>
      ) : null}
      <FlatList
        data={listData}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        ItemSeparatorComponent={AHSeparator}
        ListFooterComponent={AHSeparator}
        ListHeaderComponent={!canAdd ? AHSeparator : null}
      />
      {canAdd ? (
        <ActionFooter
          safeInsetBottom
          primary={
            <ActionFooter.Primary
              title={intl_tokens.Add}
              icon="tv"
              onPress={() => {
                props.navigation.navigateDeprecated("AddScreen", {});
              }}
            />
          }
        />
      ) : null}
    </View>
  );
};

export const StorecastAdminDeviceList = (props: StorecastDeviceListProps) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <StorecastAdminDeviceListComponent
          navigation={props.navigation}
          fetchKey={fetchKey}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
};
